import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/Card';
import { Toast } from '../components/ui/Toast';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { listResortAdminUsers, createResortAdminUser, updateResortAdminUser, getResortLocations, listRestaurants, listGroceries, listResortAdminRoles, resendPasswordToken } from '../services/resortAdminApi';
import { UserPlus, KeyIcon, Edit2, Mail, ChevronLeft, ChevronRight } from 'lucide-react';
import { isGlobalRole, noMerchantSelection } from '../utils/adminRoles';
import { courierSelectableMerchant } from '../utils/merchantSelection';

interface UsersPaginationProps {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

const UsersPagination: React.FC<UsersPaginationProps> = ({ page, pageSize, total, onPageChange, onPageSizeChange }) => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const startItem = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, total);
  const goTo = (p: number) => {
    if (p >= 1 && p <= totalPages) onPageChange(p);
  };
  return (
    <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-4">
      <div className="flex-1 flex justify-between sm:hidden">
        <button onClick={() => goTo(page - 1)} disabled={page <= 1} className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">Previous</button>
        <button onClick={() => goTo(page + 1)} disabled={page >= totalPages} className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">Next</button>
      </div>
      <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
        <p className="text-sm text-gray-700">
          Showing <span className="font-medium">{startItem}</span> to <span className="font-medium">{endItem}</span> of <span className="font-medium">{total}</span> users
        </p>
        <div className="flex items-center space-x-2">
          <label htmlFor="users-page-size" className="text-sm text-gray-700">Show:</label>
          <select
            id="users-page-size"
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="block w-20 px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-alps-primary focus:border-alps-primary"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <div className="flex items-center space-x-1">
            <button onClick={() => goTo(page - 1)} disabled={page <= 1} className="relative inline-flex items-center px-2 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-500 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
              <ChevronLeft className="h-4 w-4" />
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum: number;
              if (totalPages <= 5) pageNum = i + 1;
              else if (page <= 3) pageNum = i + 1;
              else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
              else pageNum = page - 2 + i;
              return (
                <button
                  key={pageNum}
                  onClick={() => goTo(pageNum)}
                  className={`relative inline-flex items-center px-4 py-2 text-sm font-medium border rounded-md ${pageNum === page ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'}`}
                >
                  {pageNum}
                </button>
              );
            })}
            <button onClick={() => goTo(page + 1)} disabled={page >= totalPages} className="relative inline-flex items-center px-2 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-500 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const Settings: React.FC = () => {
  const navigate = useNavigate();
  const {
    user,
    isReadOnly,
    changePassword
  } = useAuth();
  const isSuperAdmin = user?.role === 'Resort Super Admin';
  const canViewUsers = isSuperAdmin || isReadOnly;
  const canEditUsers = isSuperAdmin && !isReadOnly;
  const [activeTab, setActiveTab] = useState<'users' | 'adduser' | 'password'>(canViewUsers ? 'users' : 'password');

  // Keep the active tab on something the current role is allowed to see. The
  // role can resolve/refresh after mount (the cached login-time role may be
  // stale), so a tab chosen for a higher privilege must fall back to Change
  // Password — otherwise its gated content renders nothing and the page looks
  // blank ("totally empty").
  useEffect(() => {
    if (activeTab === 'users' && !canViewUsers) setActiveTab('password');
    if (activeTab === 'adduser' && !canEditUsers) setActiveTab('password');
  }, [activeTab, canViewUsers, canEditUsers]);
  
  // Resort Admin Users state
  const [resortAdminUsers, setResortAdminUsers] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [usersPage, setUsersPage] = useState(1);
  const [usersPageSize, setUsersPageSize] = useState(25);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersSearch, setUsersSearch] = useState('');
  const [, setResortLocations] = useState<Array<{ value: string; label: string }>>([]);
  const [restaurants, setRestaurants] = useState<Array<{ name: string; restaurant_name: string; click_and_collect?: number; delivery_enabled?: number }>>([]);
  const [groceries, setGroceries] = useState<Array<{ name: string; grocery_name: string; click_and_collect?: number; delivery_enabled?: number }>>([]);
  const [availableRoles, setAvailableRoles] = useState<string[]>([]);
  const [allRoles, setAllRoles] = useState<string[]>([]);
  const [resendingToken, setResendingToken] = useState<string | null>(null);

  // User management states
  const [newUserForm, setNewUserForm] = useState({
    email: '',
    role: 'Partner Admin',
    scope_type: 'all' as 'restaurant' | 'grocery' | 'all',
    restaurants: [] as string[],
    groceries: [] as string[],
  });
  const [userError, setUserError] = useState('');
  const [userSuccess, setUserSuccess] = useState('');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Password change states
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  // Load resort locations, restaurants, groceries, and roles
  useEffect(() => {
    const loadData = async () => {
      try {
        // Load resort locations
        const locationsResult = await getResortLocations();
        if (locationsResult && locationsResult.resort_locations) {
          setResortLocations(locationsResult.resort_locations);
        }

        // Load restaurants
        const restaurantsResult = await listRestaurants(undefined, 1, 1000); // Get all restaurants
        if (restaurantsResult && restaurantsResult.data) {
          setRestaurants(restaurantsResult.data);
        }

        // Load groceries
        const groceriesResult = await listGroceries(undefined, 1, 1000); // Get all groceries
        if (groceriesResult && groceriesResult.data) {
          setGroceries(groceriesResult.data);
        }

        // Load available roles from server (already excludes "Resort Super Admin")
        const rolesResult = await listResortAdminRoles();
        if (rolesResult && Array.isArray(rolesResult)) {
          // All roles from server (already excludes "Resort Super Admin")
          // This includes: Partner Admin, Partner Courier, Support
          setAllRoles(rolesResult);
          // Use all roles for both create and edit dropdowns
          setAvailableRoles(rolesResult);
          // Set default role if available
          if (rolesResult.length > 0 && !rolesResult.includes(newUserForm.role)) {
            setNewUserForm({ ...newUserForm, role: rolesResult[0] });
          }
        } else {
          console.error('Failed to load roles or invalid format:', rolesResult);
        }
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };
    loadData();
  }, []);

  // Load Resort admin users (only for Super Admin / Supervisor)
  const loadResortAdminUsers = async () => {
    if (!canViewUsers) return;
    setLoadingUsers(true);
    try {
      const result = await listResortAdminUsers(usersSearch || undefined, undefined, usersPage, usersPageSize);
      if (result && result.data) {
        setResortAdminUsers(result.data);
        setUsersTotal(result.pagination?.total ?? result.data.length);
      }
    } catch (error) {
      console.error('Error loading Resort admin users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Load users on mount and when page / page size changes
  useEffect(() => {
    if (canViewUsers) {
      loadResortAdminUsers();
    }
  }, [canViewUsers, usersPage, usersPageSize]);

  // Debounced refresh when the search query changes (resets to page 1)
  useEffect(() => {
    if (!canViewUsers) return;
    const handle = setTimeout(() => {
      if (usersPage !== 1) {
        setUsersPage(1); // triggers reload via the effect above
      } else {
        loadResortAdminUsers();
      }
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersSearch]);

  // Redirect non-super-admins away from users tab
  useEffect(() => {
    if (!canViewUsers && activeTab === 'users') {
      setActiveTab('password');
    }
    if (!canEditUsers && activeTab === 'adduser') {
      setActiveTab('password');
    }
  }, [canViewUsers, canEditUsers, activeTab]);

  // Handle resending password token
  const handleResendToken = async (userName: string) => {
    setResendingToken(userName);
    setUserError('');
    setUserSuccess('');
    try {
      await resendPasswordToken(userName);
      setUserSuccess('Password reset link has been resent successfully.');
      await loadResortAdminUsers();
    } catch (error: any) {
      setUserError(error.message || 'Failed to resend password token');
    } finally {
      setResendingToken(null);
    }
  };

  // Handle adding a new user
  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setUserError('');
    setUserSuccess('');
    if (!newUserForm.email) {
      setUserError('Email is required');
      return;
    }
    // Global roles (and Support) need no merchant assignment — skip the checks.
    if (!noMerchantSelection(newUserForm.role)) {
      if (newUserForm.scope_type === 'restaurant' && newUserForm.restaurants.length === 0) {
        setUserError('At least one restaurant is required');
        return;
      }
      if (newUserForm.scope_type === 'grocery' && newUserForm.groceries.length === 0) {
        setUserError('At least one grocery is required');
        return;
      }
      if (newUserForm.scope_type === 'all' && newUserForm.restaurants.length === 0 && newUserForm.groceries.length === 0) {
        setUserError('At least one restaurant or grocery is required');
        return;
      }
    }
    try {
      await createResortAdminUser({
        email: newUserForm.email,
        role: newUserForm.role,
        scope_type: newUserForm.scope_type as 'restaurant' | 'grocery',
        restaurants: !noMerchantSelection(newUserForm.role) && (newUserForm.scope_type === 'restaurant' || newUserForm.scope_type === 'all') ? newUserForm.restaurants : undefined,
        groceries: !noMerchantSelection(newUserForm.role) && (newUserForm.scope_type === 'grocery' || newUserForm.scope_type === 'all') ? newUserForm.groceries : undefined,
      });
      const msg = 'User added successfully. The user will receive an email to set their password.';
      setUserSuccess(msg);
      setToast({ message: msg, type: 'success' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setNewUserForm({
        email: '',
        role: 'Partner Admin',
        scope_type: 'all',
        restaurants: [],
        groceries: [],
      });
      // Reload users list
      await loadResortAdminUsers();
    } catch (error: any) {
      const errMsg = error.message || 'Failed to add user. Email may already be in use.';
      setUserError(errMsg);
      setToast({ message: errMsg, type: 'error' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Handle deleting a user

  // Handle changing user role
  const handleRoleChange = async (email: string, role: string) => {
    setUserError('');
    setUserSuccess('');
    // The backend forbids setting "Resort Super Admin" via API. It only appears in
    // the dropdown so a Super Admin user's current role displays correctly (and
    // isn't silently overwritten); re-selecting the same value doesn't fire
    // onChange, so this only guards an attempted promotion of another user.
    if (role === 'Resort Super Admin') {
      return;
    }
    try {
      await updateResortAdminUser({
        name: email,
        role: role as any
      });
      setUserSuccess('User role updated successfully');
      setToast({ message: 'User role updated successfully', type: 'success' });
      await loadResortAdminUsers();
    } catch (error: any) {
      const errMsg = error.message || 'Failed to update user role';
      setUserError(errMsg);
      setToast({ message: errMsg, type: 'error' });
    }
  };

  // Handle password change
  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters long');
      return;
    }
    const success = await changePassword(passwordForm.currentPassword, passwordForm.newPassword);
    if (success) {
      setPasswordSuccess('Password changed successfully');
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } else {
      setPasswordError('Current password is incorrect');
    }
  };

  // The server role list excludes "Resort Super Admin". Surface it as an option
  // only when the current user is a Super Admin, so a Super Admin user's row
  // shows their real role instead of the <select> snapping to a different one
  // (which would overwrite/lose the role on change). handleRoleChange ignores it,
  // since the backend forbids assigning it via API.
  const roleOptions =
    isSuperAdmin && !allRoles.includes('Resort Super Admin')
      ? ['Resort Super Admin', ...allRoles]
      : allRoles;

  return (
    <div className="space-y-6">
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
      <div>
        <h1 className="text-2xl font-semibold text-gray-800">Settings</h1>
        <p className="mt-1 text-sm text-gray-600">
          Manage application settings
        </p>
      </div>
      {/* Tabs */}
      <div className="flex space-x-4 border-b border-gray-200">
        {canViewUsers && (
          <button
            onClick={() => setActiveTab('users')}
            className={`py-2 px-4 ${
              activeTab === 'users'
                ? 'border-b-2 border-alps-primary text-alps-primary'
                : 'text-gray-500'
            }`}
          >
          User Management
        </button>
        )}
        {canEditUsers && (
          <button
            onClick={() => setActiveTab('adduser')}
            className={`py-2 px-4 ${
              activeTab === 'adduser'
                ? 'border-b-2 border-alps-primary text-alps-primary'
                : 'text-gray-500'
            }`}
          >
          Add User
        </button>
        )}
        <button
          onClick={() => setActiveTab('password')}
          className={`py-2 px-4 ${
            activeTab === 'password'
              ? 'border-b-2 border-alps-primary text-alps-primary'
              : 'text-gray-500'
          }`}
        >
          Change Password
        </button>
      </div>
      {/* User Management Tab */}
      {activeTab === 'users' && canViewUsers && (
        <div className="space-y-6">
          <Card title="Manage Users">
            <div className="mb-4">
              <input
                type="text"
                value={usersSearch}
                onChange={(e) => setUsersSearch(e.target.value)}
                placeholder="Search by email or role…"
                className="block w-full sm:w-80 border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
              />
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Email
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Role
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Scope
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Status
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loadingUsers ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                        Loading users...
                      </td>
                    </tr>
                  ) : resortAdminUsers.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                        No users found
                      </td>
                    </tr>
                  ) : (
                    resortAdminUsers.map((u) => (
                      <tr key={u.name}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {u.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {u.email === user?.email || !canEditUsers ? (
                            <span className="capitalize">{u.role}</span>
                          ) : (
                            <select
                              value={u.role}
                              onChange={(e) =>
                                handleRoleChange(u.email, e.target.value)
                              }
                              className="border border-gray-300 rounded-md shadow-sm py-1 px-2 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                            >
                              {roleOptions.length > 0 ? (
                                roleOptions.map((role) => (
                                  <option key={role} value={role}>
                                    {role}
                                  </option>
                                ))
                              ) : (
                                <>
                                  <option value={u.role}>{u.role}</option>
                                  <option value="Partner Admin">Partner Admin</option>
                                  <option value="Partner Courier">Partner Courier</option>
                                  <option value="Support">Support</option>
                                </>
                              )}
                            </select>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          <div className="space-y-1">
                            {u.restaurant && (
                              <div>Restaurants: {u.restaurant}</div>
                            )}
                            {u.grocery && (
                              <div>Groceries: {u.grocery}</div>
                            )}
                            {!u.restaurant && !u.grocery && '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <span
                            className={`px-2 py-1 text-xs rounded-full ${
                              u.status === 'Active'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {u.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => navigate(`/settings/users/${u.name}`)}
                              className="text-indigo-600 hover:text-indigo-900"
                              title="Edit user"
                            >
                              <Edit2 className="h-5 w-5" />
                            </button>
                            {u.has_expired_token && u.status === 'Disabled' && (
                              <button
                                onClick={() => handleResendToken(u.name)}
                                disabled={resendingToken === u.name || isReadOnly}
                                className="text-blue-600 hover:text-blue-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Resend password link"
                              >
                                <Mail className="h-5 w-5" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {usersTotal > 0 && (
              <UsersPagination
                page={usersPage}
                pageSize={usersPageSize}
                total={usersTotal}
                onPageChange={setUsersPage}
                onPageSizeChange={(size) => {
                  setUsersPageSize(size);
                  setUsersPage(1);
                }}
              />
            )}
          </Card>

        </div>
      )}
      {/* Add User Tab */}
      {activeTab === 'adduser' && canEditUsers && (
        <Card title="Add Resort Admin User">
          {userError && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
              <div className="flex">
                <div className="ml-3">
                  <p className="text-sm text-red-700">{userError}</p>
                </div>
              </div>
            </div>
          )}
          {userSuccess && (
            <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-4">
              <div className="flex">
                <div className="ml-3">
                  <p className="text-sm text-green-700">{userSuccess}</p>
                </div>
              </div>
            </div>
          )}
          <form onSubmit={handleAddUser} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <input
                type="email"
                id="email"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                value={newUserForm.email}
                onChange={(e) =>
                  setNewUserForm({ ...newUserForm, email: e.target.value })
                }
                required
              />
            </div>
            <div>
              <label htmlFor="role" className="block text-sm font-medium text-gray-700">
                Role
              </label>
              <select
                id="role"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                value={newUserForm.role}
                onChange={(e) => {
                  const selectedRole = e.target.value;
                  // Automatically set scope_type based on role
                  let newScopeType: 'restaurant' | 'grocery' | 'all' = newUserForm.scope_type;
                  let newRestaurants = newUserForm.restaurants;
                  let newGroceries = newUserForm.groceries;
                  if (selectedRole === 'Partner Admin' || selectedRole === 'Partner Courier' || noMerchantSelection(selectedRole)) {
                    newScopeType = 'all';
                  }
                  // Global/Support roles carry no merchant assignment.
                  if (noMerchantSelection(selectedRole)) {
                    newRestaurants = [];
                    newGroceries = [];
                  }
                  setNewUserForm({
                    ...newUserForm,
                    role: selectedRole,
                    scope_type: newScopeType,
                    restaurants: newRestaurants,
                    groceries: newGroceries,
                  });
                }}
              >
                {availableRoles.map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="scope_type" className="block text-sm font-medium text-gray-700">
                Scope Type
              </label>
              <select
                id="scope_type"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                value={newUserForm.scope_type}
                onChange={(e) =>
                  setNewUserForm({
                    ...newUserForm,
                    scope_type: e.target.value as 'restaurant' | 'grocery' | 'all',
                    restaurants: e.target.value === 'grocery' ? [] : newUserForm.restaurants,
                    groceries: e.target.value === 'restaurant' ? [] : newUserForm.groceries,
                  })
                }
                disabled={newUserForm.role === 'Partner Admin' || newUserForm.role === 'Partner Courier' || noMerchantSelection(newUserForm.role)}
              >
                <option value="restaurant">Restaurant</option>
                <option value="grocery">Grocery</option>
                {(newUserForm.role === 'Partner Admin' || newUserForm.role === 'Partner Courier' || noMerchantSelection(newUserForm.role)) && (
                  <option value="all">All (Restaurants & Groceries)</option>
                )}
              </select>
              {isGlobalRole(newUserForm.role) ? (
                <p className="mt-1 text-xs text-gray-500">This role has full access to all restaurants and groceries — no selection needed.</p>
              ) : (newUserForm.role === 'Partner Admin' || newUserForm.role === 'Partner Courier' || newUserForm.role === 'Support') && (
                <p className="mt-1 text-xs text-gray-500">Scope type is automatically set based on the selected role</p>
              )}
            </div>
            {(newUserForm.scope_type === 'restaurant' || newUserForm.scope_type === 'all') && !noMerchantSelection(newUserForm.role) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Restaurants (you can select multiple) <span className="text-red-500">*</span>
                </label>
                <div className="mt-1 border border-gray-300 rounded-md shadow-sm max-h-48 overflow-y-auto p-2">
                  {restaurants
                    .filter((x) => courierSelectableMerchant(x, newUserForm.role, newUserForm.restaurants))
                    .map((restaurant) => (
                    <label key={restaurant.name} className="flex items-center py-1 px-2 hover:bg-gray-50 rounded">
                      <input
                        type="checkbox"
                        className="mr-2 h-4 w-4 text-alps-primary focus:ring-alps-primary border-gray-300 rounded"
                        checked={newUserForm.restaurants.includes(restaurant.name)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setNewUserForm({
                              ...newUserForm,
                              restaurants: [...newUserForm.restaurants, restaurant.name],
                            });
                          } else {
                            setNewUserForm({
                              ...newUserForm,
                              restaurants: newUserForm.restaurants.filter((r) => r !== restaurant.name),
                            });
                          }
                        }}
                      />
                      <span className="text-sm text-gray-700">
                        {restaurant.restaurant_name || restaurant.name}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            {(newUserForm.scope_type === 'grocery' || newUserForm.scope_type === 'all') && !noMerchantSelection(newUserForm.role) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Groceries (you can select multiple) <span className="text-red-500">*</span>
                </label>
                <div className="mt-1 border border-gray-300 rounded-md shadow-sm max-h-48 overflow-y-auto p-2">
                  {groceries
                    .filter((x) => courierSelectableMerchant(x, newUserForm.role, newUserForm.groceries))
                    .map((grocery) => (
                    <label key={grocery.name} className="flex items-center py-1 px-2 hover:bg-gray-50 rounded">
                      <input
                        type="checkbox"
                        className="mr-2 h-4 w-4 text-alps-primary focus:ring-alps-primary border-gray-300 rounded"
                        checked={newUserForm.groceries.includes(grocery.name)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setNewUserForm({
                              ...newUserForm,
                              groceries: [...newUserForm.groceries, grocery.name],
                            });
                          } else {
                            setNewUserForm({
                              ...newUserForm,
                              groceries: newUserForm.groceries.filter((g) => g !== grocery.name),
                            });
                          }
                        }}
                      />
                      <span className="text-sm text-gray-700">
                        {grocery.grocery_name || grocery.name}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div>
              <button
                type="submit"
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-alps-primary hover:bg-alps-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-alps-primary"
              >
                <UserPlus className="mr-2 h-4 w-4" />
                Add User
              </button>
            </div>
          </form>
        </Card>
      )}
      {/* Change Password Tab */}
      {activeTab === 'password' && (
        <Card title="Change Password">
          {passwordError && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
              <div className="flex">
                <div className="ml-3">
                  <p className="text-sm text-red-700">{passwordError}</p>
                </div>
              </div>
            </div>
          )}
          {passwordSuccess && (
            <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-4">
              <div className="flex">
                <div className="ml-3">
                  <p className="text-sm text-green-700">{passwordSuccess}</p>
                </div>
              </div>
            </div>
          )}
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div>
              <label
                htmlFor="currentPassword"
                className="block text-sm font-medium text-gray-700"
              >
                Current Password
              </label>
              <input
                type="password"
                id="currentPassword"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                value={passwordForm.currentPassword}
                onChange={(e) =>
                  setPasswordForm({
            ...passwordForm,
            currentPassword: e.target.value
                  })
                }
                required
              />
            </div>
            <div>
              <label
                htmlFor="newPassword"
                className="block text-sm font-medium text-gray-700"
              >
                New Password
              </label>
              <input
                type="password"
                id="newPassword"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                value={passwordForm.newPassword}
                onChange={(e) =>
                  setPasswordForm({
            ...passwordForm,
            newPassword: e.target.value
                  })
                }
                required
              />
            </div>
            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-gray-700"
              >
                Confirm New Password
              </label>
              <input
                type="password"
                id="confirmPassword"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                value={passwordForm.confirmPassword}
                onChange={(e) =>
                  setPasswordForm({
            ...passwordForm,
            confirmPassword: e.target.value
                  })
                }
                required
              />
            </div>
            <div>
              <button
                type="submit"
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-alps-primary hover:bg-alps-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-alps-primary"
              >
                <KeyIcon className="mr-2 h-4 w-4" />
                Change Password
              </button>
            </div>
          </form>
        </Card>
      )}
    </div>
  );
};
